import React, { useState, useEffect, useRef } from 'react';
import { CharacterCard } from '../types';
import { getDoc, doc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import { useRouter } from './Router';
import { useToast } from '../hooks/useToast';
import { useTheme } from '../contexts/ThemeContext';
import Header from './Header';
import InteractiveCard3D from './InteractiveCard3D';
import { convertFirestoreExifData } from '../utils/dataUtils';
import { cardSaveService } from '../services/CardSaveService';
import { classifyColor } from '../utils/colorUtils';
import { getCardCache } from '../utils/cardCache';
import { safeFormatDate, formatDateTime } from '../utils/dateFormatting';
import { scrollToY } from '../utils/scrolling';
import { isSupportedCard } from '../utils/supportedCardTypes';
import { buildDescriptionEditUpdate, applyLocalDescriptionEdit, parseDescriptionMeta } from '../utils/descriptionMeta';
import { parseGenerationMetadata } from '../utils/generationMetadata';

interface CardDetailPageProps {
  cardId: string;
}

interface UserData {
  id: string;
  email: string;
  displayName: string;
  photoURL: string;
  createdAt: Date;
  updatedAt: Date;
}

const CardDetailPage: React.FC<CardDetailPageProps> = ({ cardId }) => {
  const [card, setCard] = useState<CharacterCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const { currentUser } = useAuth();
  const { navigate } = useRouter();
  const { showToast } = useToast();
  const { theme } = useTheme();

  const isOwner = currentUser && card && currentUser.id === card.userId;

  // Description編集機能
  const startEditDescription = () => {
    const currentDescription = card?.characterData?.description_ja || '';
    setEditDescription(currentDescription);
    setIsEditingDescription(true);
  };

  const cancelEditDescription = () => {
    setIsEditingDescription(false);
    setEditDescription('');
  };

  const saveDescription = async () => {
    if (!card || !currentUser || !isOwner) return;
    
    try {
      setSavingDescription(true);
      
      // Firestoreのカードドキュメントを更新
      // descriptionMeta はドットパスで部分更新（editCount は increment、時刻は serverTimestamp）。
      // 所有者専用のAI原文には触れない。descriptionMeta の無いレガシーカードは editCount 1 から始まる
      const cardRef = doc(db, 'cards', card.id);
      const editedAt = new Date();
      await updateDoc(cardRef, buildDescriptionEditUpdate(editDescription, { increment, serverTimestamp }));

      // ローカル状態を更新（リロード不要で source / editCount / updatedAt を反映）
      setCard(prevCard => prevCard ? {
        ...prevCard,
        characterData: {
          ...prevCard.characterData!,
          description_ja: editDescription
        },
        descriptionMeta: applyLocalDescriptionEdit(prevCard.descriptionMeta, editedAt),
        updatedAt: editedAt,
      } : null);
      
      setIsEditingDescription(false);
      setEditDescription('');
      showToast('Description updated successfully', 'success');
      
    } catch (error) {
      showToast('Failed to update description', 'error');
    } finally {
      setSavingDescription(false);
    }
  };

  const prevCardIdRef = useRef<string | null>(null);

  useEffect(() => {
    setTimeout(() => {
      scrollToY(0);
    }, 0);

    const fetchCard = async () => {
      try {
        const isNewCard = prevCardIdRef.current !== cardId;
        if (!isNewCard && card) return;

        if (isNewCard) {
          prevCardIdRef.current = cardId;

          // キャッシュから即表示を試みる
          const cached = getCardCache(cardId);
          if (cached) {
            if (!isSupportedCard(cached)) {
              setCard(null);
              setError('Card not found');
              setLoading(false);
              return;
            }
            setCard(cached);
            setLoading(false);
            setError(null);
            return;
          }

          setLoading(true);
          setError(null);
        }
        // キャッシュになければFirestoreから取得
        const cardDoc = await getDoc(doc(db, 'cards', cardId));

        if (!cardDoc.exists()) {
          setError('Card not found');
          setLoading(false);
          return;
        }

        const cardData = cardDoc.data();
        if (!isSupportedCard(cardData)) {
          setError('Card not found');
          setLoading(false);
          return;
        }
        const cardWithDates = {
          id: cardDoc.id,
          ...cardData,
          createdAt: cardData.createdAt || new Date(),
          updatedAt: cardData.updatedAt || new Date(),
          exifData: convertFirestoreExifData(cardData.exifData),
          // 既存カードには無い → undefined（後方互換）。不正値も安全に落とす
          generationMetadata: parseGenerationMetadata(cardData.generationMetadata),
          descriptionMeta: parseDescriptionMeta(cardData.descriptionMeta),
        } as CharacterCard;

        setCard(cardWithDates);
        setLoading(false);
      } catch (err) {
        setError('Failed to load card');
        setLoading(false);
      }
    };

    fetchCard();
  }, [cardId]);

  const handleDownload = () => {
    if (card?.pixelArtUrl) {
      const link = document.createElement('a');
      link.href = card.pixelArtUrl;
      link.download = `pixel-art-${card.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('Download started', 'success');
    }
  };

  const handleDeleteCard = async () => {
    if (!card || !isOwner || !currentUser) return;
    
    if (window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
      try {
        // CardSaveServiceを使って削除（カウント更新も含む）
        const { cardSaveService } = await import('../services/CardSaveService');
        await cardSaveService.deleteCard(card.id, card, currentUser.id);
        
        showToast('Card deleted successfully', 'success');
        navigate('/');
      } catch (error) {
        showToast('Failed to delete card', 'error');
      }
    }
  };


  const getKeyword = () => {
    if (!card.characterData || !card.characterData.mainObjects || card.characterData.mainObjects.length === 0) {
      return null;
    }
    return card.characterData.mainObjects[0];
  };


  if (loading) {

    return (
      <div className="min-h-screen p-4 sm:p-8 pb-16 sm:pb-20 font-sans flex flex-col bg-theme-primary">
        <Header />
        
        <main className="flex-grow">
          <div className="card-detail-container">
            {/* Skeleton Card Image */}
            <div className="card-image-section">
              <div className="card-image relative w-full bg-theme-tertiary overflow-hidden animate-pulse rounded-2xl" style={{aspectRatio: '3/4.25'}}>
                {/* Main image area */}
                <div className={`w-full h-full bg-theme-tertiary animate-pulse`}></div>
                
                {/* Bottom overlay for date and keyword */}
                <div className="absolute bottom-6 left-6 z-10 space-y-2">
                  <div className={`h-5 bg-theme-tertiary rounded animate-pulse w-24`}></div>
                  <div className={`h-8 bg-theme-tertiary rounded animate-pulse w-32`}></div>
                </div>
              </div>
            </div>
            
            {/* Skeleton Details Section */}
            <div className="card-details-section space-y-6">
              {/* Date Skeleton */}
              <div className={`h-3 bg-theme-tertiary rounded animate-pulse w-36`}></div>

              {/* Description Skeleton */}
              <div className="space-y-3">
                <div className={`h-4 bg-theme-tertiary rounded animate-pulse w-full`}></div>
                <div className={`h-4 bg-theme-tertiary rounded animate-pulse w-5/6`}></div>
                <div className={`h-4 bg-theme-tertiary rounded animate-pulse w-3/4`}></div>
              </div>

              {/* Keywords Skeleton */}
              <div className="flex flex-wrap gap-1">
                <div className={`h-6 bg-theme-tertiary rounded-full animate-pulse w-20`}></div>
                <div className={`h-6 bg-theme-tertiary rounded-full animate-pulse w-16`}></div>
                <div className={`h-6 bg-theme-tertiary rounded-full animate-pulse w-24`}></div>
                <div className={`h-6 bg-theme-tertiary rounded-full animate-pulse w-14`}></div>
              </div>

              {/* Colors Skeleton */}
              <div className="flex flex-wrap gap-1">
                <div className={`w-6 h-6 bg-theme-tertiary rounded-md animate-pulse`}></div>
                <div className={`w-6 h-6 bg-theme-tertiary rounded-md animate-pulse`}></div>
                <div className={`w-6 h-6 bg-theme-tertiary rounded-md animate-pulse`}></div>
                <div className={`w-6 h-6 bg-theme-tertiary rounded-md animate-pulse`}></div>
                <div className={`w-6 h-6 bg-theme-tertiary rounded-md animate-pulse`}></div>
              </div>
            </div>
          </div>
        </main>
        
        <style>{`
          .card-detail-container {
            max-width: 800px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 2rem;
          }
          
          .card-image-section {
            width: 100%;
            display: flex;
            justify-content: center;
          }
          
          .card-details-section {
            width: 100%;
            padding: 1rem 0;
          }
          
          @media (min-width: 1024px) {
            .card-detail-container {
              flex-direction: row;
              gap: 2rem;
              justify-content: center;
            }
            
            .card-image-section {
              flex: 0 0 auto;
              width: 28rem;
            }
            
            .card-details-section {
              flex: 0 0 auto;
              width: 18rem;
              padding: 0;
            }
          }
        `}</style>
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen bg-theme-primary flex items-center justify-center">
        <div className="text-center">
          <div className="text-status-error text-xl mb-4">{error || 'Card not found'}</div>
          <button
            onClick={() => navigate('/')}
            className="text-brand-primary hover:text-brand-hover underline"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const { description, description_ja, mainColors, mainObjects } = card.characterData;
  const displayDescription = description_ja || description;
  const hasExifData = card.exifData && (card.exifData.createDate || card.exifData.model || (card.exifData.latitude && card.exifData.longitude));

  return (
    <div className="min-h-screen p-4 sm:p-8 pb-16 sm:pb-20 font-sans flex flex-col bg-theme-primary">
      <Header />
      
      <main className="flex-grow">
        <div className="card-detail-container">
          {/* Card Image Section */}
          <div className="card-image-section">
            <InteractiveCard3D
              className="w-full h-full"
              isEnabled={true}
            >
              <div className="card-image">
              <div className="relative">
                {card.pixelArtUrl && (
                  <img
                    src={card.pixelArtUrl}
                    alt={mainObjects.join(', ')}
                    className="w-full h-auto pixelated"
                    style={{ imageRendering: 'pixelated' }}
                  />
                )}
                {/* Date and Keyword Labels */}
                <div className="absolute bottom-6 left-6 z-10">
                  <div className="fira-sans-book-italic text-white text-xl opacity-90 drop-shadow-lg mb-2 tracking-wider">
                    {safeFormatDate(card.exifData?.createDate || card.createdAt)}
                  </div>
                  {getKeyword() && (
                    <span className="card-keyword-display fira-sans-extra-bold-italic">
                      {getKeyword()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            </InteractiveCard3D>
          </div>

          {/* Details Section */}
          <div className="card-details-section">
            {/* Post Date */}
            <div className="mb-4">
              <p className="text-xs text-gray-500">
                {formatDateTime(card.exifData?.createDate || card.createdAt)}
              </p>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 hidden">Description</h4>
              {isEditingDescription ? (
                <div className="space-y-3">
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        cancelEditDescription();
                      } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                        e.preventDefault();
                        if (editDescription.trim() !== '' && !savingDescription) {
                          saveDescription();
                        }
                      }
                    }}
                    className="w-full px-3 py-2 bg-theme-secondary border border-theme-primary rounded-lg text-theme-primary placeholder-theme-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none text-sm"
                    rows={4}
                    maxLength={500}
                    placeholder="Enter a description for your creation..."
                    autoFocus
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-theme-tertiary">
                      {editDescription.length}/500 characters
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={cancelEditDescription}
                        className="px-3 py-1.5 text-sm text-theme-secondary hover:text-theme-primary transition-colors"
                        disabled={savingDescription}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveDescription}
                        disabled={savingDescription || editDescription.trim() === ''}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          savingDescription || editDescription.trim() === ''
                            ? 'bg-theme-secondary text-theme-tertiary cursor-not-allowed'
                            : 'bg-brand-primary hover:bg-brand-hover text-white'
                        }`}
                      >
                        {savingDescription ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start space-x-2">
                  <p className="text-theme-secondary leading-relaxed text-left text-sm flex-1">
                    {displayDescription}
                  </p>
                  {isOwner && (
                    <button
                      onClick={startEditDescription}
                      className="flex-shrink-0 p-2 text-theme-tertiary hover:text-brand-primary hover:bg-theme-hover rounded-md transition-all duration-200 mt-0.5 ml-1 touch-manipulation"
                      aria-label="Edit description"
                      title="Edit description"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Tags & Colors */}
            <div className="mb-8">
              <div className="space-y-4">
                {mainObjects.length > 0 && (() => {
                  const stopWords = new Set(['a','an','the','of','in','on','at','to','for','with','and','or','is','are','was','were','be','been','it','its','this','that','from','by','as','into','about','no','not','very','just','small','large','big','little','old','new','young','long','short','high','low']);
                  const words = new Set<string>();
                  mainObjects.forEach(obj => {
                    obj.toLowerCase().split(/[\s,.\-\/]+/).forEach(w => {
                      if (w.length > 1 && !stopWords.has(w)) words.add(w);
                    });
                  });
                  return (
                    <div className="flex flex-wrap gap-1.5">
                      {Array.from(words).map(word => (
                        <a
                          key={word}
                          href={`/tags/${encodeURIComponent(word)}`}
                          onClick={(e) => { e.preventDefault(); navigate(`/tags/${encodeURIComponent(word)}`); }}
                          className="px-2.5 py-1 text-xs rounded-full bg-theme-secondary text-theme-secondary hover:bg-brand-primary hover:text-white transition-colors"
                        >
                          #{word}
                        </a>
                      ))}
                    </div>
                  );
                })()}
                <div className="flex flex-wrap gap-1">
                  {mainColors.map((color, index) => {
                    const closest = classifyColor(color);
                    return (
                      <a
                        key={index}
                        href={`/colors/${closest}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/colors/${closest}`); }}
                        className="w-5 h-5 rounded-md border border-gray-600 shadow-sm cursor-pointer hover:scale-125 transition-transform"
                        style={{ backgroundColor: color }}
                        aria-label={closest}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Private Information - Only for owner */}
            {isOwner && (
              <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-zinc-700' : 'bg-theme-secondary'} border-theme-primary`}>
                <h4 className="text-sm font-bold text-red-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Private Info
                </h4>
                
                {/* Photo Details */}
                {hasExifData && card.exifData && (
                  <div className="mb-4">
                    <ul className="text-sm text-theme-secondary space-y-1">
                      {card.exifData.createDate && (
                        <li className="flex items-center gap-2">
                          <svg className={`w-4 h-4 text-theme-secondary`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>{(() => {
                            try {
                              let date;
                              const createDate = card.exifData.createDate;
                              
                              // Firestore Timestamp の場合
                              if (createDate && typeof createDate === 'object' && 'toDate' in createDate) {
                                date = (createDate as any).toDate();
                              }
                              // Date オブジェクトの場合
                              else if (createDate instanceof Date) {
                                date = createDate;
                              }
                              // 文字列の場合（EXIF形式: "YYYY:MM:DD HH:MM:SS"）
                              else if (typeof createDate === 'string') {
                                const dateStr = (createDate as string).replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
                                date = new Date(dateStr);
                              }
                              // その他の場合
                              else {
                                date = new Date(createDate as any);
                              }
                              
                              
                              return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
                            } catch (error) {
                              return 'Invalid Date';
                            }
                          })()}</span>
                        </li>
                      )}
                      {card.exifData.model && (
                        <li className="flex items-center gap-2">
                          <svg className={`w-4 h-4 text-theme-secondary`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{card.exifData.model}</span>
                        </li>
                      )}
                      {(card.exifData.latitude !== undefined || card.exifData.longitude !== undefined) && (
                        <li className="flex items-center gap-2">
                          <svg className={`w-4 h-4 text-theme-secondary`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <div className="flex items-center gap-2">
                            {(typeof card.exifData.latitude === 'number' && 
                              typeof card.exifData.longitude === 'number' && 
                              !isNaN(card.exifData.latitude) && 
                              !isNaN(card.exifData.longitude)) ? (
                              <>
                                <a 
                                  href={`https://www.google.com/maps?q=${card.exifData.latitude},${card.exifData.longitude}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-brand-primary hover:underline"
                                >
                                  View on Map
                                </a>
                                <span className="text-xs text-gray-500">
                                  ({card.exifData.latitude.toFixed(4)}, {card.exifData.longitude.toFixed(4)})
                                </span>
                              </>
                            ) : (
                              <span className="text-gray-500">Location available</span>
                            )}
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleDeleteCard}
                    className="flex items-center gap-1 px-2 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <style>{`
        .card-detail-container {
          max-width: 800px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .card-image-section {
          width: 100%;
        }

        .card-image {
          background-color: #111827;
          border-radius: 1rem;
          overflow: hidden;
        }

        .card-keyword-display {
          font-family: 'Fira Sans', 'Inter', system-ui, -apple-system, sans-serif;
          font-weight: 800;
          font-style: italic;
          color: white;
          text-transform: capitalize;
          padding-right: 20px;
          text-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          line-height: 1;
          font-size: 2rem;
        }

        .card-details-section {
          width: 100%;
          padding: 1rem 0;
        }

        @media (min-width: 1024px) {
          .card-detail-container {
            flex-direction: row;
            gap: 2rem;
            justify-content: center;
          }

          .card-image-section {
            flex: 0 0 auto;
            width: 28rem;
          }

          .card-image {
            border-radius: 1rem;
          }

          .card-keyword-display {
            font-size: 2.75rem;
          }

          .card-details-section {
            flex: 0 0 auto;
            width: 20rem;
            padding: 0;
          }
        }

        @media (min-width: 768px) {
          .card-image {
            border-radius: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CardDetailPage;
